// ============================================================
// STANCES TO ALWAYS ON AND SOLO MODES (plan modes-rework, 2026-10-27) -
// an older sheet's `stanceGroups`, read where a sheet comes in
// (`normalizeCharacter`) and never written again.
//
//   a switchable stance -> a Solo mode; in a group that held one at a
//                          time, it keeps that group (one at a time)
//   a triggered stance  -> an Always on item with its trigger
//
// IDS ARE KEPT: what a combatant has switched on (`activeStances`) still
// names its Solo modes, and an `own_stance` condition still matches by
// name. A sheet that already has the new lists keeps them: the old groups
// are only read when neither list is there (idempotent).
//
// TWO OLD BEHAVIOURS GO, by the rulings: a group that was not marked Solo
// stood up in Squad Mode - every Solo mode now stands down; and a trigger
// in a one-at-a-time group pushed its switched fellow off - as Always on it
// now stands beside it.
//
// Pure. Zero React dependency.
// ============================================================
import { readOwnState } from './effectWhen';
import { readAlwaysOn, readSoloModes, type EffectItem, type SoloMode } from './soloModes';

type Raw = Record<string, unknown>;

/**
 * One old group's stances, reshaped and not yet read: a trigger this build knows to Always on, the rest to Solo
 * modes, in the group when it held one at a time (it did unless it said otherwise).
 */
function reshape(group: Raw | null, out: { alwaysOn: unknown[]; soloModes: unknown[] }): void {
  const oneAtATime = group?.exclusive !== false ? { group: { id: group?.id, name: group?.name } } : {};
  for (const stance of Array.isArray(group?.stances) ? (group.stances as Raw[]) : []) {
    if (readOwnState(stance?.whileOwn)) out.alwaysOn.push(stance);
    else out.soloModes.push({ ...stance, ...oneAtATime });
  }
}

/**
 * The new lists an older sheet's stance groups become, read through the same readers as any stored list (so the ids,
 * names and origins follow the same rules); null when there is nothing to migrate - no groups, or the sheet already
 * carries either new list.
 */
export function migrateStances(sheet: Raw): { alwaysOn: EffectItem[]; soloModes: SoloMode[] } | null {
  if (!Array.isArray(sheet.stanceGroups) || Array.isArray(sheet.alwaysOn) || Array.isArray(sheet.soloModes))
    return null;
  const out = { alwaysOn: [] as unknown[], soloModes: [] as unknown[] };
  for (const group of sheet.stanceGroups) reshape(group as Raw | null, out);
  return { alwaysOn: readAlwaysOn(out.alwaysOn), soloModes: readSoloModes(out.soloModes) };
}
